import {
  DragDropContext,
  Draggable,
  Droppable,
  DraggableLocation,
} from "@hello-pangea/dnd"
import { Group, Text } from "@mantine/core"
import { IconGripVertical } from "@tabler/icons-react"
import cx from "clsx"
import { useEffect, ReactNode } from "react"

import { ActionIcon } from "../../../atoms/ActionIcons/ActionIcon"
import { useStableListState } from "../../../utils/useStableListState"

import classes from "./DragNDrop.module.css"

export type DraggableItem = {
  id: string
  position?: number
  label: string | ReactNode
  cta?: ReactNode
}

export type DragEndResult = {
  destination?: DraggableLocation | null
  item: DraggableItem
}

export type DragNDropProps = {
  data: DraggableItem[]
  onDragEnd?: (result: DragEndResult) => void
  draggableHandle?: boolean
  deletable?: boolean
  onDelete?: (id: string) => void
  showPosition?: boolean
}

export function DragNDrop({
  data,
  onDragEnd,
  draggableHandle = true,
  deletable = false,
  onDelete,
  showPosition = false,
}: DragNDropProps) {
  const [state, dispatch] = useStableListState(
    showPosition
      ? data.sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
      : data
  )

  useEffect(() => {
    const newState = showPosition
      ? data.sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
      : data
    dispatch?.({ type: "SET_STATE", payload: newState })
  }, [data, showPosition, dispatch])

  const items = state.map((item, index) => (
    <Draggable key={item.id} index={index} draggableId={item.id}>
      {(provided, snapshot) => (
        <Group
          className={cx(classes.item, {
            [classes.itemDragging]: snapshot.isDragging,
          })}
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...(!draggableHandle && provided.dragHandleProps)}
        >
          <Group>
            {draggableHandle && (
              <div {...provided.dragHandleProps} className={classes.dragHandle}>
                <IconGripVertical size={18} stroke={1.5} />
              </div>
            )}
            {showPosition && item.position && <Text>{item.position}</Text>}
            {typeof item.label === "string" ? (
              <Text>{item.label}</Text>
            ) : (
              item.label
            )}
          </Group>
          <Group gap={0}>
            {item.cta && item.cta}
            {deletable && (
              <ActionIcon
                type="delete"
                onClick={() => {
                  dispatch?.({ type: "REMOVE", payload: [index] })
                  dispatch?.({
                    type: "APPLY",
                    payload: (item, index) => {
                      if (index === undefined) return item
                      return {
                        ...item,
                        position: index + 1,
                      }
                    },
                  })
                  onDelete?.(state[index].id)
                }}
              />
            )}
          </Group>
        </Group>
      )}
    </Draggable>
  ))

  return (
    <DragDropContext
      onDragEnd={({ destination, source }) => {
        if (!destination) return
        onDragEnd?.({ item: state[source.index], destination })
        dispatch?.({
          type: "REORDER",
          payload: { from: source.index, to: destination.index },
        })
        dispatch?.({
          type: "APPLY",
          payload: (item, index) => {
            if (index === undefined) return item
            return {
              ...item,
              position: index + 1,
            }
          },
        })
      }}
    >
      <Droppable droppableId="dnd-list" direction="vertical">
        {(provided) => (
          <div {...provided.droppableProps} ref={provided.innerRef}>
            {items}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  )
}
